import { api } from "./axiosInstance";

/**Login Function
 * Logs in user with email/password
 * @param {string} email
 * @param {string} password
 * @param {boolean} [rememberMe]
 * @return {Promise} Resolves with auth token and user data
 * @throws {Error} When login fails
 */
export const loginWithEmailAndPassword = async (
  email,
  password,
  rememberMe
) => {

  try {
    const res = await api.post("/access-tokens", {
      email,
      password
    });

    const { token, user } = res.data;

    if (rememberMe) {
      localStorage.setItem("accessToken", token);
    } else {
      sessionStorage.setItem("accessToken", token);
    }

    return {
      status: "success",
      token,
      user,
    };
  } catch (err) {
    return {
      status: "error",
      message: err.response?.data?.message || "Login failed",
    };
  }
};

/**Logout Function
 * Logs out current user
 * @return {Promise} Resolves when logout completes
 * @throws {Error} When logout fails
 */
export const logout = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    await api.delete(`/access-tokens/${token}`);
    localStorage.removeItem("accessToken");
    sessionStorage.removeItem("accessToken");
  } catch (err) {
    return {
      status: "error",
      message: err.response?.data?.message || "Logout failed",
    };
  }
};

/**AuthenticatedUser Function
 * Gets current user profile
 * @return {Promise} Resolves with user data
 * @throws {Error} When unauthorized or request fails
 */
export const getAuthenticatedUser = async () => {
  try {
    const res = await api.get("/user");
    return {
      status: "success",
      user: res.data,
    };
  } catch (err) {
    console.error("Fetch User Error:", err.response?.data || err);
    throw err;
  }
};

/**Register Function
 * Creates new user account
 * @param {Object} data - Registration data
 * @param {string} data.name - Full name
 * @param {string} data.email - Valid email
 * @param {string} data.password - Minimum 8 characters
 * @param {string} data.password_confirmation - Must match password
 * @param {string} [data.country_code] - Country code (e.g. '+970')
 * @param {string} [data.phone_number] - Phone number with country code
 * @return {Promise} Resolves with registration result
 * @throws {Error} When validation or registration fails
 **/
export const registerWithEmailAndPassword = async (data) => {
  try {
    const res = await api.post("/users", {
      name: data.name,
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation,
      country_code: data.country_code,
      phone_number: data.phone_number,
    });
    return {
      status: "success",
      message: res.message,
    };
  } catch (err) {
    return {
      status: "error",
      message: err.response?.data?.message || "Register failed",
    };
  }
};

//ForgotPassword Function
export const ForgotPassword = async (email) => {
  try {
    const res = await api.post("/auth/password/reset/code", {
      email,
    });
    return {
      data: res.data,
      status: "success",
    };
  } catch (err) {
    console.log(err.response?.data?.message);
    return {
      error: err.response?.data?.message,
      status: "error",
    };
  }
};

//ResetPassword Function
export const ResetPassword = async (code) => {
  try {
    const res = await api.post("/auth/password/reset", {
      code,
    });

    return {
      data: res.data,
      status: "success",
    };
  } catch (err) {
    console.log(err.response?.data?.message);
    return {
      error: err.response?.data?.message,
      status: "error",
    };
  }
};

//UpdatePassword Function
export const UpdatePassword = async (newPassword) => {
  const resetUrl = localStorage.getItem("resetUrl");
  const resetCode = localStorage.getItem("resetCode");

  try {
    await api.patch(resetUrl, {
      code: resetCode,
      password: newPassword,
    });

    return {
      status: "success",
    };
  } catch (err) {
    console.log(err.response?.data?.message);
    return {
      error: err.response?.data?.message,
      status: "error",
    };
  }
};

//SendEmailCode Function
export const SendEmailCode = async () => {
  try {
    await api.post("/auth/email/verification");

    return {
      status: "success",
    };
  } catch (err) {
    console.log(err.response?.data?.message);
    return {
      error: err.response?.data?.message,
      status: "error",
    };
  }
};

//VerifyEmailCode Function
export const VerifyEmailCode = async (code) => {
  try {
    const res = await api.patch("/auth/email/verification", {
      code,
    });

    return {
      data: res.data,
      status: "success",
    };
  } catch (err) {
    console.log(err.response?.data?.message);
    return {
      error: err.response?.data?.message,
      status: "error",
    };
  }
};
